// Файл: src/utils/errorReporting.js
// Единая точка отправки ошибок фронта. Все перехватчики (ErrorBoundary,
// window.onerror, unhandledrejection) зовут только reportClientError —
// канал доставки подключается здесь, в одном месте.

const SENT = new Map();          // подпись ошибки -> время последней отправки
const DEDUP_MS = 10 * 60 * 1000; // одну и ту же ошибку шлём не чаще раза в 10 минут
let transport = null;

// Подключить канал доставки: fn({ message, stack, componentStack, url, ... })
export function setErrorTransport(fn) {
  transport = typeof fn === 'function' ? fn : null;
}

const signature = (error, extra) => {
  // Without a stack, the browser's original coordinates distinguish failures.
  const location = error?.stack ? '' : `${extra.filename || ''}:${extra.lineno || 0}:${extra.colno || 0}`;
  return `${error?.name || 'Error'}:${String(error?.message || error).slice(0, 200)}:${location}`;
};

const coordinate = (value) => Number.isSafeInteger(value) && value > 0 ? value : 0;

export function reportClientError(error, extra = {}) {
  try {
    const sig = signature(error, extra);
    const now = Date.now();
    if (now - (SENT.get(sig) || 0) < DEDUP_MS) return;
    SENT.set(sig, now);

    const payload = {
      message: String(error?.message || error || 'Unknown error').slice(0, 1000),
      name: error?.name || 'Error',
      stack: String(error?.stack || '').slice(0, 4000),
      componentStack: String(extra.componentStack || '').slice(0, 4000),
      source: extra.source || 'unknown',
      filename: String(extra.filename || '').slice(0, 500),
      lineno: coordinate(extra.lineno),
      colno: coordinate(extra.colno),
      detailsUnavailable: extra.detailsUnavailable === true,
      url: window.location.pathname + window.location.search,
      userAgent: navigator.userAgent,
      release: process.env.REACT_APP_RELEASE || '',
      at: new Date().toISOString(),
    };

    // eslint-disable-next-line no-console
    console.error('[client-error]', payload.source, error);
    if (transport) transport(payload);
  } catch {
    // Отправка ошибки не должна ронять приложение
  }
}

// Ошибки вне React-рендера: обработчики событий, таймеры, промисы
export function installGlobalErrorHandlers() {
  const onError = (event) => {
    // Resource events have no JavaScript exception and must not become fake errors.
    if (typeof event.message !== 'string') return;
    const detailsUnavailable = !event.error && /^Script error\.?$/i.test(event.message.trim()) &&
      !event.filename && !event.lineno && !event.colno;
    // Creating an Error here would point its stack at this reporter, not the failure.
    reportClientError(event.error || { name: 'Error', message: event.message, stack: '' }, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      detailsUnavailable,
    });
  };
  const onRejection = (event) => {
    const reason = event.reason && typeof event.reason === 'object'
      ? event.reason : { name: 'Error', message: String(event.reason), stack: '' };
    reportClientError(reason, { source: 'unhandledrejection' });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
